import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { DateTime } from "luxon";
import { SupportedLanguages } from "@/types/dictionary";
import { sanitizeTrackingParam } from "./tracking-params";

export const MEETING_TIMEZONE = "Asia/Jerusalem";
export const MEETING_START_TIME = "10:00";  
export const MEETING_END_TIME = "18:00";
export const MEETING_DURATION = 30;
export const DATE_FORMAT = "dd-MM-yyyy";
export const TIME_FORMAT = "HH:mm";
export const DATE_FORMAT_WITH_TIME = `${DATE_FORMAT} ${TIME_FORMAT}`;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getIsRtl(lang: SupportedLanguages): boolean {
  return lang === SupportedLanguages.HE;
}

export function isHebrewText(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

export function formatDateForDisplay(date: string): string {
  try {
    const parsedDate = DateTime.fromFormat(date, "yyyy-MM-dd");
    return parsedDate.toFormat(DATE_FORMAT);
  } catch (error) {
    console.error('Error formatting date:', error);
    return date;
  }
}

export function splitInto30MinSlots(date: string, startTime: string, endTime: string, userTimezone: string) {
  const slots = [];
  let currentTime = DateTime.fromFormat(`${date} ${startTime}`, "yyyy-MM-dd HH:mm", { zone: userTimezone });
  const endDateTime = DateTime.fromFormat(`${date} ${endTime}`, "yyyy-MM-dd HH:mm", { zone: userTimezone });

  while (currentTime < endDateTime) {
    const israelTimeSlot = currentTime.setZone(MEETING_TIMEZONE);
    const endTimeSlot = israelTimeSlot.plus({ minutes: MEETING_DURATION });
    
    slots.push({
      date: israelTimeSlot.toFormat("yyyy-MM-dd"),
      start_time: israelTimeSlot.toFormat("HH:mm"),
      end_time: endTimeSlot.toFormat("HH:mm"),
      start_date: israelTimeSlot.toISO(),
      end_date: endTimeSlot.toISO(),
      timezone: israelTimeSlot,
    });
    
    currentTime = currentTime.plus({ minutes: MEETING_DURATION });
  }

  return slots;
}

export const getUTMParamsFromLocalStorage = () => {
  // Keep in sync with UTM_KEYS in components/UTMCapture.tsx. Previously this
  // dropped fbclid/gclid/landing_referrer even though UTMCapture stored them,
  // which blanked out paid-ad (Meta/Google) attribution on every lead.
  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'device',
    'fbclid',
    'gclid',
  ];

  const params: Record<string, string> = {};

  keys.forEach((key) => {
    // Sanitized on read as well as on write: storage written before the
    // sanitizer shipped can still hold literal "{{campaign.name}}" macros.
    const value = sanitizeTrackingParam(localStorage.getItem(key));
    if (value) {
      params[key] = value;
    }
  });

  // First-touch referrer is stored under its own key (and may be an empty
  // string, which marks "arrived cold" - skip those so it reads as Direct).
  const referrer = localStorage.getItem('landing_referrer');
  if (referrer) {
    params.landing_referrer = referrer;
  }

  return params;
};